import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, Check, X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { useIsMobile } from '@/shared/mobile';
import { resolvePatternText, type PatternText } from '@/shared/patterns/text';
import { Button } from '@/shared/ui/button';
import { cn } from '@/shared/ui/cn';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/shared/ui/drawer';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet';

/** Uma etapa do mini-trilho do painel. */
export interface FlowPanelStep {
  readonly id: string;
  readonly title: PatternText;
}

export interface FlowPanelTrailProps {
  readonly steps: readonly FlowPanelStep[];
  readonly current: string;
  /** As concluídas; sem a lista, as anteriores à atual. */
  readonly completed?: readonly string[] | ReadonlySet<string> | undefined;
  readonly className?: string | undefined;
}

/** O mini-trilho do painel ("Dados · Revisão · Código"): a atual marcada, as concluídas com ✓ e o estado em texto. */
export function FlowPanelTrail({ steps, current, completed, className }: FlowPanelTrailProps) {
  const { i18n, t } = useLingui();
  const index = Math.max(0, steps.findIndex((step) => step.id === current));
  const done = (id: string, position: number) => (completed ? (completed instanceof Set ? completed.has(id) : (completed as readonly string[]).includes(id)) : position < index);
  return (
    <ol aria-label={t`Etapas`} data-slot="flow-panel-trail" className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs', className)}>
      {steps.map((step, position) => {
        const state = position === index ? 'current' : done(step.id, position) ? 'done' : 'pending';
        return (
          <li key={step.id} className="flex items-center gap-1.5" {...(state === 'current' ? { 'aria-current': 'step' as const } : {})}>
            {position > 0 ? (
              <span aria-hidden className="text-muted-foreground">
                ·
              </span>
            ) : null}
            <span className={cn(state === 'current' ? 'font-semibold text-accent-text' : state === 'done' ? 'text-foreground' : 'text-muted-foreground')}>
              {state === 'done' ? <Check aria-hidden className="mr-0.5 inline size-3" strokeWidth={3} /> : null}
              {resolvePatternText(i18n, step.title)}
            </span>
            <span className="sr-only">{state === 'done' ? t`concluído` : state === 'current' ? t`atual` : t`não iniciado`}</span>
          </li>
        );
      })}
    </ol>
  );
}

export interface FlowPanelProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** Sem descrição, o título faz as vezes dela para o leitor de tela. */
  readonly description?: ReactNode;
  /** As etapas do mini-trilho; com menos de duas, o trilho não aparece. */
  readonly steps?: readonly FlowPanelStep[] | undefined;
  readonly current?: string | undefined;
  readonly completed?: FlowPanelTrailProps['completed'];
  /** O "Voltar" padrão, à esquerda; sem ele, o botão de saída (`dismissLabel`). */
  readonly onBack?: (() => void) | undefined;
  /** "Voltar" à vista, mas desligado (a regra do legado que trava a volta). */
  readonly backDisabled?: boolean | undefined;
  /** O rótulo da saída quando não há etapa anterior ("Fechar", "Cancelar"); `null` esconde. */
  readonly dismissLabel?: string | null | undefined;
  /** Os botões da etapa, à direita; o principal por último (no iPhone ele fica em cima). */
  readonly actions?: ReactNode;
  /** Ocupado: voltar, sair e fechar ficam travados. */
  readonly busy?: boolean | undefined;
  readonly size?: 'sm' | 'md' | undefined;
  /** `data-slot` do conteúdo, para teste e leitor. */
  readonly slot?: string | undefined;
  readonly bodyClassName?: string | undefined;
  readonly children: ReactNode;
}

/**
 * O painel do Fluxo guiado (U53, discovery/13 §3): `Sheet` à direita na web e `Drawer` no iPhone, com o mini-trilho no
 * topo, o conteúdo da etapa rolando e o rodapé fixo com "Voltar" (ou a saída) à esquerda e as ações à direita.
 */
export function FlowPanel({
  open,
  onOpenChange,
  title,
  description,
  steps,
  current,
  completed,
  onBack,
  backDisabled = false,
  dismissLabel,
  actions,
  busy = false,
  size = 'sm',
  slot,
  bodyClassName,
  children,
}: FlowPanelProps) {
  const { t } = useLingui();
  const isMobile = useIsMobile();
  const trail = steps && steps.length > 1 ? <FlowPanelTrail steps={steps} current={current ?? steps[0]?.id ?? ''} completed={completed} className="mt-1" /> : null;
  const dismiss = dismissLabel === undefined ? t`Fechar` : dismissLabel;
  const leading = onBack ? (
    <Button type="button" variant="secondary" disabled={busy || backDisabled} onClick={onBack}>
      <ArrowLeft aria-hidden />
      {t`Voltar`}
    </Button>
  ) : dismiss === null ? null : (
    <Button type="button" variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
      {dismiss}
    </Button>
  );
  const change = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };
  const hasFooter = leading !== null || actions !== undefined;
  // A chave pela etapa remonta os botões: sem ela, o React reaproveita o botão clicado e, se na etapa nova ele vira o
  // `submit` de outro formulário, o mesmo clique envia esse formulário.
  const stepActions = <Fragment key={current ?? ''}>{actions}</Fragment>;

  if (isMobile)
    return (
      <Drawer open={open} onOpenChange={change} repositionInputs={false}>
        <DrawerContent data-slot={slot ?? 'flow-panel'} className="max-h-[92dvh]">
          <DrawerHeader className={cn(onBack && 'relative pr-12')}>
            {/* Com "Voltar" no rodapé, a saída do iPhone vira o X do cabeçalho (a web já tem o do Sheet). */}
            {onBack ? (
              <Button type="button" variant="ghost" size="icon" aria-label={t`Fechar`} disabled={busy} className="absolute top-2 right-3 size-10 text-muted-foreground" onClick={() => onOpenChange(false)}>
                <X aria-hidden />
              </Button>
            ) : null}
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription className={cn(!description && 'sr-only')}>{description ?? title}</DrawerDescription>
            {trail}
          </DrawerHeader>
          <DrawerBody className={cn('flex flex-col gap-4', bodyClassName)}>{children}</DrawerBody>
          {hasFooter ? (
            <DrawerFooter data-slot="flow-panel-footer" className="flex-col-reverse border-t border-border pt-3">
              {leading}
              {stepActions}
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>
    );
  return (
    <Sheet open={open} onOpenChange={change}>
      <SheetContent data-slot={slot ?? 'flow-panel'} size={size}>
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className={cn(!description && 'sr-only')}>{description ?? title}</SheetDescription>
          {trail}
        </SheetHeader>
        <SheetBody className={cn('flex flex-col gap-4', bodyClassName)}>{children}</SheetBody>
        {hasFooter ? (
          <SheetFooter data-slot="flow-panel-footer" className="sm:justify-start">
            {leading}
            <div className="flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row sm:flex-wrap sm:justify-end">{stepActions}</div>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
